/**
 * On-read migration helpers for ADR-0055 — turn pre-cutover legacy
 * stays / journey anchors (with flat name/address/lat/lng fields) into
 * the unified `place: PlaceRef` shape the rest of the app now expects.
 *
 * Idempotent: calling these on an already-migrated entity is a no-op.
 * Synthetic id uses the `'legacy:'` source prefix so future tooling can
 * recognise un-re-picked entities and skip catalog lookups against
 * them.
 *
 * Run from the store hydration path (sync inbox, persisted load,
 * imported trips) so readers downstream always see `place`.
 */

import type { JourneyAnchor, PlaceRef, Stay, Trip } from './types';

/** Legacy shape — pre-ADR-0055 stays / anchors carried these as
 *  siblings of the (then-absent) `place`. Used only here for the
 *  migration read path; new code should treat Stay/JourneyAnchor as
 *  having `place: PlaceRef` only. */
interface LegacyPlaceFields {
  name?: string | undefined;
  address?: string | undefined;
  lat?: number | undefined;
  lng?: number | undefined;
  catalogId?: string | undefined;
  googlePlaceId?: string | undefined;
}

function buildLegacyPlace(syntheticId: string, legacy: LegacyPlaceFields): PlaceRef {
  // Prefer the existing catalog id when one was stored (post-ADR-0044
  // stays already carry a `<source>:<native>` id). Otherwise fall back
  // to the synthetic `legacy:` id derived from the parent entity.
  const id = legacy.catalogId
    || (legacy.googlePlaceId ? `google:${legacy.googlePlaceId}` : syntheticId);
  return {
    id,
    name: legacy.name ?? 'Unknown',
    address: legacy.address,
    lat: legacy.lat,
    lng: legacy.lng,
  };
}

export function migrateStayPlace(stay: Stay): Stay {
  // Already migrated.
  if (stay.place) return stay;
  // Defensive read of the legacy fields — they were typed away when the
  // schema cut over, but a server-persisted record predating the cutover
  // still has them.
  const legacy = stay as unknown as Stay & LegacyPlaceFields;
  return {
    ...stay,
    place: buildLegacyPlace(`legacy:${stay.id}`, legacy),
  };
}

export function migrateJourneyAnchorPlace(
  anchor: JourneyAnchor,
  journeyId: string,
  side: 'origin' | 'destination',
): JourneyAnchor {
  if (anchor.place) return anchor;
  const legacy = anchor as unknown as JourneyAnchor & LegacyPlaceFields;
  return {
    ...anchor,
    place: buildLegacyPlace(`legacy:${journeyId}:${side}`, legacy),
  };
}

/** Migrate every Stay / Journey on a Trip in one pass. Cheap when
 *  everything is already migrated — the per-entity helpers short-circuit
 *  on `place` presence. */
export function migrateTripPlaces(trip: Trip): Trip {
  const stays = trip.stays?.map(migrateStayPlace);
  const journeys = trip.journeys?.map((j) => ({
    ...j,
    origin: migrateJourneyAnchorPlace(j.origin, j.id, 'origin'),
    destination: migrateJourneyAnchorPlace(j.destination, j.id, 'destination'),
  }));
  // Also pull through legacy day-level journeys (ADR-0043 v2 migration
  // window) so they get the same treatment.
  const days = trip.days?.map((d) => {
    if (!d.journeys?.length) return d;
    return {
      ...d,
      journeys: d.journeys.map((j) => ({
        ...j,
        origin: migrateJourneyAnchorPlace(j.origin, j.id, 'origin'),
        destination: migrateJourneyAnchorPlace(j.destination, j.id, 'destination'),
      })),
    };
  });
  return {
    ...trip,
    ...(stays ? { stays } : {}),
    ...(journeys ? { journeys } : {}),
    ...(days ? { days } : {}),
  };
}
